import type { PaymentServiceApiModel } from '@api_patient/generated/clientv2/models';
import { PaymentApiService } from '@api_patient/payment_api.service';
import { HttpError } from '@application/http_error';
import { UniversalConfig } from '@application/universal_config.service';
import { ImperativeSheetRef } from '@cellula/react';
import { LocalPushNotification } from '@component/notification_manager/notification/local_push_notification';
import { NotificationService } from '@component/notification_manager/notification/notification.service';
import { AnalyticService, AnalyticCategories } from '@application/analytic/analytic.service';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { UnhandledIssuesService } from '@feature/issues/unhandled_issues.service';
import { Injectable } from '@visiba/instantiation';
import { Presentation } from '@visiba/presentation';
import { EventEmitter, Timer } from '@visiba/std';
import { SHEET_CLOSE_INTO_NOTIFICATION_WAIT } from '../../constants';
import { RefCallback } from 'react';

export enum PaymentState {
	PaymentOptions,
	PaymentFrame,
	PaymentFailed,
	PaymentCanceled,
	PaymentCompleted,
	PaymentCompletedNoReceipt,
}

export interface AttemptData {
	receiptUrl: string | null;
	hasReceipt: boolean;
}

interface PresentationModel {
	availableOptions: PaymentServiceApiModel[];
	selectedPaymentOption: PaymentServiceApiModel | null;
	currentStep: PaymentState;
	errorMessage: Nullable<string>;
	attemptData: AttemptData | null;
	issueTrackingId: string | null;
}

export interface MessageEventData {
	event: 'Success' | 'Error' | 'Cancel';
	data: {
		info: AttemptData;
		message: string;
		messageDetails?: string;
	};
}

@Injectable()
export class PaymentController {
	public readonly events = new EventEmitter<{
		onSuccess: AttemptData;
		onAbort: void;
	}>();
	public readonly presentation = Presentation.create<PresentationModel>({
		availableOptions: [],
		selectedPaymentOption: null,
		errorMessage: null,
		currentStep: PaymentState.PaymentOptions,
		attemptData: null,
		issueTrackingId: null,
	});
	public readonly state = Presentation.create<{ state: 'unpaid' | 'paid' | 'alreadyPaid' }>({
		state: 'unpaid',
	});
	private billId: number | null = null;

	private imperativeSheetRef: ImperativeSheetRef | null = null;
	private readonly bundMessageListenerFn: (message: MessageEvent<MessageEventData>) => void;

	constructor(
		private readonly paymentApiService: PaymentApiService,
		private readonly universalConfig: UniversalConfig,
		private readonly notificationService: NotificationService,
		private readonly unhandledIssuesService: UnhandledIssuesService,
		private readonly analyticService: AnalyticService,
	) {
		this.bundMessageListenerFn = this.handlePaymentMessage.bind(this);
	}

	public createRefBridge(): RefCallback<ImperativeSheetRef> {
		return (ref) => {
			this.imperativeSheetRef = ref;
		};
	}

	public async loadBill(billId: number, trackedByIssueId: string | null = null): Promise<void> {
		this.billId = billId;

		this.presentation.write({
			currentStep: PaymentState.PaymentOptions,
			availableOptions: [],
			selectedPaymentOption: null,
			errorMessage: null,
			issueTrackingId: trackedByIssueId,
		});

		this.removeListener();
		globalThis.addEventListener('message', this.bundMessageListenerFn);

		const response = await this.paymentApiService.getServices(this.universalConfig.selectedUnitId, billId);
		if (response.err) {
			this.handleLoadBillError(response.err);

			return;
		}

		this.presentation.write({ availableOptions: response.ok.data });
	}

	public handlePaymentMessage(message: MessageEvent<MessageEventData>): void {
		switch (message.data?.event) {
			case 'Success':
				this.setSuccess(message.data?.data.info);
				break;
			case 'Error':
				if (message.data?.data != null) {
					if (message.data.data.messageDetails === 'AlreadyPaid') {
						this.state.write({ state: 'alreadyPaid' });
						break;
					}
					this.setError(message.data.data.message);
				} else {
					this.setError('Unknown error');
				}
				break;
			case 'Cancel':
				this.setCancel();

				break;
		}
	}

	public tearDown(): void {
		this.removeListener();
	}

	public selectPaymentMethod(option: PaymentServiceApiModel): void {
		this.presentation.write({ errorMessage: null, selectedPaymentOption: option, currentStep: PaymentState.PaymentFrame });
	}

	public async alreadyPaid(): Promise<void> {
		let paymentAttemptData: AttemptData = { hasReceipt: false, receiptUrl: null };

		if (this.billId) {
			const paymentResult = await this.paymentApiService.getPayment(this.billId);
			if (paymentResult.ok && paymentResult.ok.data.State === 'Paid') {
				const receiptUrl = paymentResult.ok.data.ReceiptUrl;
				paymentAttemptData = { hasReceipt: !!receiptUrl, receiptUrl };
			}
		}

		this.closeSheet();
		this.events.emit('onSuccess', paymentAttemptData);

		const { issueTrackingId } = this.presentation.read();
		if (issueTrackingId != null) {
			this.unhandledIssuesService.removeArbitraryIssue(issueTrackingId, 'awaitPayment');
		}
	}

	public cancel(): void {
		this.closeSheet();
		this.events.emit('onAbort', undefined);

		if (this.presentation.read().currentStep === PaymentState.PaymentOptions) return;

		Timer.wait(() => {
			this.notificationService.add(
				new LocalPushNotification({
					label: I18nAndL10n.translate('PW.Component.Visits.Payment.Toast.CancelledPayment'),
					variant: 'warning',
					type: 'toast',
				}),
			);
		}, SHEET_CLOSE_INTO_NOTIFICATION_WAIT);
	}

	private setSuccess(info: AttemptData): void {
		this.analyticService.trackEvent(AnalyticCategories.PaymentFlow, 'Finished payment');

		this.presentation.write({ currentStep: PaymentState.PaymentCompleted, attemptData: info });

		this.closeSheet();
		this.events.emit('onSuccess', info);

		Timer.wait(() => {
			this.notificationService.add(
				new LocalPushNotification({
					label: I18nAndL10n.translate('PW.Component.Visits.Payment.Toast.SuccessfulPayment'),
					variant: 'success',
					type: 'toast',
				}),
			);

			const { issueTrackingId } = this.presentation.read();
			if (issueTrackingId != null) {
				this.unhandledIssuesService.removeArbitraryIssue(issueTrackingId, 'awaitPayment');
			}
		}, SHEET_CLOSE_INTO_NOTIFICATION_WAIT);
	}

	private closeSheet(): void {
		this.imperativeSheetRef?.close();
	}

	private removeListener(): void {
		globalThis.removeEventListener('message', this.bundMessageListenerFn);
	}

	private setError(error: string): void {
		this.analyticService.trackEvent(AnalyticCategories.PaymentFlow, 'Got error from iframe');

		this.presentation.write({ errorMessage: error, currentStep: PaymentState.PaymentFailed, selectedPaymentOption: null });
	}

	private setCancel(): void {
		this.presentation.write({ currentStep: PaymentState.PaymentCanceled, selectedPaymentOption: null });
	}

	private handleLoadBillError(error: HttpError<unknown>): void {
		this.presentation.write({ errorMessage: error.message });

		this.analyticService.trackEvent(AnalyticCategories.PaymentFlow, 'Got error from Visiba');

		Timer.wait(() => {
			this.notificationService.add(
				new LocalPushNotification({
					label: I18nAndL10n.translate('PW.Component.Visits.Payment.Toast.FailedPayment'),
					variant: 'danger',
					type: 'toast',
				}),
			);
		}, SHEET_CLOSE_INTO_NOTIFICATION_WAIT);
	}
}
